import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, CLUB_OPTIONS, isRegistrationClosed } from "@/lib/constants";
import { COUNTRIES } from "@/lib/countries";
import { CITIES_BY_COUNTRY, citiesFor } from "@/lib/cities";
import { ArrowRight, ArrowLeft, Upload, Check, X, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import EventDateBadge from "@/components/EventDateBadge";
import HeroWordmark from "@/components/HeroWordmark";

const buildSteps = (t) => [
  { section: t("sectionApplicant"), index: 1, total: 4, field: "full_name", label: t("qFullName"), placeholder: t("qFullNamePh"), type: "text", required: true },
  { section: t("sectionApplicant"), index: 1, total: 4, field: "age", label: t("qAge"), placeholder: t("qAgePh"), type: "number", required: true, min: 18, max: 99 },
  { section: t("sectionApplicant"), index: 1, total: 4, field: "mobile", label: t("qMobile"), placeholder: t("qMobilePh"), type: "tel", required: true },
  { section: t("sectionApplicant"), index: 1, total: 4, field: "email", label: t("qEmail"), placeholder: t("qEmailPh"), type: "email", required: true },
  { section: t("sectionApplicant"), index: 1, total: 4, field: "instagram", label: t("qInstagram"), placeholder: t("qInstagramPh"), type: "text", required: true },
  { section: t("sectionApplicant"), index: 1, total: 4, field: "origin_country", label: t("qCountry"), placeholder: t("qCountryPh"), type: "country", required: true, helper: t("qOriginHelper") },
  { section: t("sectionApplicant"), index: 1, total: 4, field: "origin_city", label: t("qCity"), placeholder: t("qCityPh"), type: "text", required: true, helper: t("qOriginHelper"), maxLength: 120 },
  { section: t("sectionVehicle"), index: 2, total: 4, field: "vehicle_make", label: t("qMake"), placeholder: t("qMakePh"), type: "text", required: true },
  { section: t("sectionVehicle"), index: 2, total: 4, field: "vehicle_model", label: t("qModel"), placeholder: t("qModelPh"), type: "text", required: true },
  { section: t("sectionVehicle"), index: 2, total: 4, field: "vehicle_year", label: t("qYear"), placeholder: t("qYearPh"), type: "text", required: true },
  { section: t("sectionVehicle"), index: 2, total: 4, field: "vehicle_color", label: t("qColor"), placeholder: t("qColorPh"), type: "text", required: true },
  { section: t("sectionVehicle"), index: 2, total: 4, field: "plate_number", label: t("qPlate"), placeholder: t("qPlatePh"), type: "text", required: true },
  { section: t("sectionVehicle"), index: 2, total: 4, field: "modifications", label: t("qMods"), placeholder: t("qModsPh"), type: "textarea", required: true },
];

export default function RegistrationFunnel() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [stepIdx, setStepIdx] = useState(-1);
  const [formData, setFormData] = useState({});
  const [photos, setPhotos] = useState({ front: null, rear: null, left: null, right: null });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rulesAgreed, setRulesAgreed] = useState([false, false, false, false, false, false, false]);
  const [phase, setPhase] = useState("questions");
  const inputRef = useRef(null);

  const STEPS = useMemo(() => buildSteps(t), [t]);
  const RULES = useMemo(() => [t("rule1"), t("rule2"), t("rule3"), t("rule4"), t("rule5"), t("rule6"), t("rule7")], [t]);

  // Bidi-safe branded lockup: visible order is `THE GATHERING` in BOTH English
  // AND Arabic contexts. THE white, GATHERING gold. Wrapped in a bidi-isolated
  // LTR span so RTL surroundings cannot reorder the two words.
  const renderWithGathering = (text) => {
    const parts = String(text).split("§GATHERING§");
    return parts.map((chunk, i, arr) => (
      <React.Fragment key={i}>
        {chunk}
        {i < arr.length - 1 && (
          <span
            dir="ltr"
            style={{ unicodeBidi: "isolate-override", direction: "ltr", display: "inline-block", whiteSpace: "nowrap" }}
          >
            {"\u202D"}THE <span className="text-[#F5C518]">GATHERING</span>{"\u202C"}
          </span>
        )}
      </React.Fragment>
    ));
  };

  useEffect(() => {
    if (stepIdx >= 0 && stepIdx < STEPS.length && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [stepIdx, phase, STEPS.length]);

  const currentStep = stepIdx >= 0 && stepIdx < STEPS.length ? STEPS[stepIdx] : null;
  const totalSteps = STEPS.length + 2;
  const progressStep =
    phase === "questions" ? stepIdx + 1 : phase === "upload" ? STEPS.length + 1 : STEPS.length + 2;
  const progress = Math.max(0, (progressStep / totalSteps) * 100);

  // Field validators — return null on OK, i18n key on failure. Client-side
  // mirror of the strict server-side pydantic validators; kept intentionally
  // permissive on phone format (international + GCC separators) so we do
  // not lock non-Saudi applicants out of the funnel.
  const AGE_MIN = 18;
  const AGE_MAX = 99;
  // Accepts: leading +, digits, spaces, hyphens, dots, parentheses. Total
  // 7-20 digits (E.164 max is 15 + separators; GCC mobiles are ~9-10 digits
  // with country code). No country whitelist.
  const MOBILE_RE = /^\+?[0-9]([0-9 .\-()]{5,25})$/;
  const MOBILE_DIGITS_RE = /[0-9]/g;
  // Pragmatic email regex — accepts anything of shape user@host.tld with
  // one @, at least one dot in the domain, no whitespace.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const validateField = (step, rawVal) => {
    if (!step) return null;
    const val = String(rawVal ?? "").trim();
    if (step.required && !val) return "requiredFieldErr";
    if (!val) return null;
    if (step.field === "age") {
      const n = Number(val);
      if (!Number.isInteger(n)) return "ageInvalid";
      if (n < AGE_MIN) return "ageMustBe18";
      if (n > AGE_MAX) return "ageInvalid";
    }
    if (step.field === "mobile") {
      if (!MOBILE_RE.test(val)) return "mobileInvalid";
      const digits = (val.match(MOBILE_DIGITS_RE) || []).length;
      if (digits < 7 || digits > 15) return "mobileInvalid";
    }
    if (step.field === "email") {
      if (!EMAIL_RE.test(val)) return "emailInvalid";
      if (val.length > 254) return "emailInvalid";
    }
    return null;
  };

  const canProceed = () => {
    if (!currentStep) return false;
    return validateField(currentStep, formData[currentStep.field]) === null;
  };

  const handleNext = () => {
    if (phase === "questions") {
      const err = validateField(currentStep, formData[currentStep?.field]);
      if (err) {
        // Preserve input, stay on current step, focus the field.
        toast.error(t(err));
        if (inputRef.current && inputRef.current.focus) inputRef.current.focus();
        return;
      }
      if (stepIdx < STEPS.length - 1) {
        setStepIdx(stepIdx + 1);
      } else {
        setPhase("upload");
      }
    } else if (phase === "upload") {
      if (!photos.front || !photos.rear || !photos.left || !photos.right) {
        toast.error(t("allPhotosRequired"));
        return;
      }
      setPhase("standards");
    }
  };

  const handleBack = () => {
    if (phase === "standards") setPhase("upload");
    else if (phase === "upload") setPhase("questions");
    else if (stepIdx > 0) setStepIdx(stepIdx - 1);
    else setStepIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && currentStep?.type !== "textarea") {
      e.preventDefault();
      handleNext();
    }
  };

  const updateField = (field, value) => {
    // Owner directive (Sept 12, 2026): changing the origin country must
    // NOT silently keep an incompatible city (e.g. Riyadh persisting into
    // a UAE application). If the new country's suggestion pool exists and
    // the previously-entered city is not in it, we clear the city so the
    // applicant re-picks or manually types. Manual (unlisted) cities are
    // preserved on same-country edits.
    if (field === "origin_country") {
      const nextCountry = String(value || "").trim();
      const prevCity = String(formData.origin_city || "").trim();
      if (prevCity) {
        const pool = citiesFor(nextCountry);
        if (pool.length > 0) {
          const known = pool.some((c) => c.en === prevCity || c.ar === prevCity);
          if (!known) {
            setFormData({ ...formData, origin_country: value, origin_city: "" });
            return;
          }
        }
      }
    }
    setFormData({ ...formData, [field]: value });
  };

  /**
   * Resize+compress a user-picked photo on the client before upload.
   * - Converts HEIC/HEIF (iPhone default) and any other format to JPEG via canvas.
   * - Caps the longest edge at 2000 px (more than enough for our display + PDF use).
   * - Targets ~1.5 MB final size with quality=0.85.
   * - If the file is already small (≤2 MB) and a supported web format, we skip resize.
   * - Returns a fresh File object with `.jpg` extension and `image/jpeg` MIME.
   * - Returns the original file as a fallback if the browser cannot decode it.
   */
  const compressImage = (file) => new Promise((resolve) => {
    try {
      const isWebFormat = /^image\/(jpe?g|png|webp)$/i.test(file.type || "");
      if (isWebFormat && file.size <= 2 * 1024 * 1024) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 2000;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width > height) {
              height = Math.round((height * MAX) / width);
              width = MAX;
            } else {
              width = Math.round((width * MAX) / height);
              height = MAX;
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) { resolve(file); return; }
              const newName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
              resolve(new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() }));
            },
            "image/jpeg",
            0.85
          );
        };
        img.onerror = () => resolve(file); // browser can't decode (rare HEIC) → upload original
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    } catch {
      resolve(file);
    }
  });

  const handleUpload = async (slot, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error(t("fileTooLarge"));
      return;
    }
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setPhotos((p) => ({ ...p, [slot]: res.data.path }));
      toast.success(t("photoUploaded"));
    } catch (e) {
      // Surface the actual failure reason so users know what to fix.
      // Common causes: Instagram in-app browser (silent failure), no internet,
      // server timeout, unsupported format.
      let msg = t("uploadFailed");
      if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e.code === "ECONNABORTED") {
        msg = "Upload timed out. Please check your connection and try again.";
      } else if (e.message?.includes("Network")) {
        msg = "Network error. If you opened this from Instagram, please tap the ••• menu and choose 'Open in Safari', then try again.";
      } else if (e.message) {
        msg = `${t("uploadFailed")} (${e.message})`;
      }
      toast.error(msg, { duration: 8000 });
      // eslint-disable-next-line no-console
      console.error("[upload] failed:", e);
    } finally {
      setUploading(false);
    }
  };

  const allRulesAgreed = rulesAgreed.every(Boolean);

  const handleSubmit = async () => {
    if (!allRulesAgreed) {
      toast.error(t("agreeAllErr"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        age: parseInt(formData.age, 10),
        modifications: formData.modifications || "",
        photo_primary: photos.front,
        photo_secondary: photos.rear,
        photo_left: photos.left,
        photo_right: photos.right,
        agreed_rules: true,
      };
      await axios.post(`${API}/applications`, payload);
      navigate("/thank-you");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("submissionFailed"));
      setSubmitting(false);
    }
  };

  // Intro screen
  if (stepIdx === -1) {
    return (
      <div className="min-h-screen bg-black relative">
        <div className="absolute top-6 right-6 sm:right-12 z-10">
          <LanguageToggle />
        </div>
        <div className="min-h-screen flex items-start justify-center px-6 sm:px-12 pt-4 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl w-full text-center"
          >
            {/* The Gathering canonical wordmark lockup (see
                HeroWordmark for the T→h alignment logic). */}
            <HeroWordmark testId="intro-title" />

            <div dir="ltr" className="flex flex-col items-center gap-4 mb-24">
              {/* Venue */}
              <span
                className="font-heading font-bold text-[#F5C518] text-3xl tracking-[0.28em] uppercase"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                VENUE
              </span>
              <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 max-w-full">
                <img
                  src="/logos/kinan-combined.png?v=1"
                  alt="Kinan"
                  className="h-[54px] sm:h-[78px] w-auto object-contain"
                  style={{ filter: "invert(1)" }}
                />
                <div className="h-[46px] sm:h-[68px] w-px bg-white/70" />
                <img
                  src="/logos/rimal-only.png?v=1"
                  alt="Rimal Center"
                  className="h-[54px] sm:h-[78px] w-auto object-contain"
                  style={{ filter: "invert(1)" }}
                />
              </div>

              {/* Partners • Sponsors */}
              <span
                className="font-heading font-bold text-[#F5C518] text-3xl tracking-[0.28em] uppercase mt-3 text-center"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                PARTNERS • SPONSORS
              </span>
              <div className="flex flex-row flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:gap-12 max-w-full">
                <img
                  src="/logos/crewznation-hero.png?v=2"
                  alt="CrewZ Nation"
                  className="h-[49.45px] sm:h-[75.79px] w-auto object-contain"
                />
                <img
                  src="/logos/sms.png?v=3"
                  alt="SMS"
                  className="h-[41.8px] sm:h-[61.6px] w-auto object-contain"
                />
                <img
                  src="/logos/fahrers-hero.png"
                  alt="The Fahrers"
                  className="h-[26px] sm:h-[42px] w-auto object-contain max-w-full"
                />
              </div>
              <div className="flex flex-row flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:gap-12 max-w-full">
                <img
                  src="/logos/incas.png?v=3"
                  alt="Incas"
                  className="h-[66px] sm:h-[100px] w-auto object-contain"
                  style={{ filter: "invert(1)" }}
                />
                <img
                  src="/logos/sto-performance.png?v=8"
                  alt="STO Performance"
                  className="h-[64px] sm:h-[96px] w-auto object-contain max-w-full"
                />
                <img
                  src="/logos/siyak.png?v=6"
                  alt="Siyak Alruyah Alalamiyah"
                  className="h-[72px] sm:h-[108px] w-auto object-contain"
                />
                <img
                  src="/logos/almurad.png?v=2"
                  alt="Almurad — Specialists in Rim Repair"
                  className="h-[72px] sm:h-[108px] w-auto object-contain -ml-[6px] sm:-ml-[10px]"
                />
              </div>
            </div>
            <div className="label-kicker mb-4" style={{ fontSize: isRTL ? "44px" : "32px" }}>{t("appForEntry")}</div>
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1] sm:leading-[1.05] lg:leading-[0.98] mb-6">
              {t("introHeadL1")} <span className="text-[#F5C518]">{t("introHeadL2")}</span>.
            </h1>
            <div className="flex justify-center mb-6">
              <EventDateBadge />
            </div>
            <button
              onClick={() => setStepIdx(0)}
              data-testid="start-questions-btn"
              className="btn-primary inline-flex items-center gap-3"
              style={{ fontSize: "29.4px" }}
            >
              {t("start")}{" "}
              {isRTL ? <ArrowRight className="w-4 h-4 rotate-180" /> : <ArrowRight className="w-4 h-4" />}
            </button>
            <button
              onClick={() => navigate("/")}
              className="block mx-auto mt-5 text-[27px] text-white/30 hover:text-white/60 tracking-[0.15em] uppercase font-body"
            >
              {t("backLanding")}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative">
      {/* Progress bar */}
      <div className={`fixed top-0 ${isRTL ? "right-0 left-0" : "left-0 right-0"} h-[3px] bg-white/5 z-50`}>
        <motion.div
          className="h-full bg-[#F5C518]"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {/* Top indicator */}
      <div className="fixed top-4 left-0 right-0 z-40 flex items-center justify-between px-6 sm:px-12">
        <div className="label-kicker" style={{ fontSize: "16px" }}>
          {phase === "questions" && currentStep && `${currentStep.section} · ${t("section")} ${currentStep.index}/4`}
          {phase === "upload" && `${t("sectionUpload")} · ${t("section")} 3/4`}
          {phase === "standards" && `${t("sectionStandards")} · ${t("section")} 4/4`}
        </div>
        <div className="flex items-center gap-5">
          <LanguageToggle />
          <div className="font-body text-base text-white/40 tracking-wider">
            {progressStep}/{totalSteps}
          </div>
        </div>
      </div>

      {/* Shared event-date badge — one instance per step, sits below the
          fixed top indicator so it doesn't disrupt vertical centering of
          the question card. */}
      <div
        className="fixed left-0 right-0 z-30 flex justify-center px-4 pointer-events-none"
        style={{ top: "3.25rem" }}
      >
        <div className="pointer-events-auto">
          <EventDateBadge />
        </div>
      </div>

      {/* Content */}
      <div className="min-h-screen flex items-center justify-center px-6 sm:px-12 pt-36 pb-24">
        <AnimatePresence mode="wait">
          {phase === "questions" && currentStep && (
            <motion.div
              key={`q-${stepIdx}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl w-full"
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="font-heading text-[#F5C518] text-3xl font-semibold">{stepIdx + 1}</span>
                <ArrowRight className={`w-4 h-4 text-[#F5C518] mt-2 ${isRTL ? "rotate-180" : ""}`} />
                <h2 className="font-heading font-semibold text-white text-2xl sm:text-3xl md:text-4xl leading-tight">
                  {currentStep.label}
                  {currentStep.required && <span className="text-[#F5C518] ms-1">*</span>}
                </h2>
              </div>

              {currentStep.helper && (
                <p
                  data-testid={`helper-${currentStep.field}`}
                  className="font-body text-white/60 text-sm sm:text-base mb-6 ms-8 leading-relaxed"
                >
                  {currentStep.helper}
                </p>
              )}

              {currentStep.type === "textarea" ? (
                <textarea
                  ref={inputRef}
                  data-testid={`input-${currentStep.field}`}
                  className="typeform-input resize-none"
                  rows={2}
                  placeholder={currentStep.placeholder}
                  value={formData[currentStep.field] || ""}
                  onChange={(e) => updateField(currentStep.field, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              ) : currentStep.type === "country" ? (
                <>
                  <input
                    ref={inputRef}
                    data-testid={`input-${currentStep.field}`}
                    list="origin-country-list"
                    className="typeform-input"
                    type="text"
                    placeholder={currentStep.placeholder}
                    value={formData[currentStep.field] || ""}
                    onChange={(e) => updateField(currentStep.field, e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="country-name"
                    maxLength={120}
                    dir={isRTL ? "rtl" : "ltr"}
                  />
                  <datalist id="origin-country-list">
                    {COUNTRIES.map((c) => (
                      <option key={c.en} value={isRTL ? c.ar : c.en}>{isRTL ? c.en : c.ar}</option>
                    ))}
                  </datalist>
                </>
              ) : currentStep.type === "select" ? (
                <div className="grid gap-2.5 sm:grid-cols-2" data-testid={`input-${currentStep.field}`}>
                  {currentStep.options.map((opt) => {
                    const selected = formData[currentStep.field] === opt.value;
                    const label = isRTL ? opt.ar : opt.en;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`group-option-${opt.value.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => updateField(currentStep.field, opt.value)}
                        className={`flex items-center justify-between gap-3 px-5 py-4 border text-left transition-all ${
                          selected
                            ? "border-[#F5C518] bg-[#F5C518]/10 text-white"
                            : "border-white/15 bg-white/[0.02] text-white/80 hover:border-white/40"
                        }`}
                      >
                        <span className="font-body text-sm sm:text-base">{label}</span>
                        <span className={`lux-checkbox ${selected ? "checked" : ""}`}>
                          {selected && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <>
                <input
                  ref={inputRef}
                  data-testid={`input-${currentStep.field}`}
                  className="typeform-input"
                  /* Owner correction (Sept 12, 2026): the DOM `input.value`
                   * reported empty by a browser-automation read-only
                   * evaluate was later confirmed by visual inspection to
                   * be a snapshot artifact — the actual UI DOES preserve
                   * the typed text on rejection. `type="text"` +
                   * `inputMode`/`autoComplete` is retained as the safer
                   * default (identical UX, no HTML type-side value
                   * handling to reason about), but no root cause was
                   * proven and no defect was verified. Do not add
                   * further changes based on the earlier speculation. */
                  type={currentStep.type === "email" || currentStep.type === "tel"
                        ? "text" : currentStep.type}
                  inputMode={
                    currentStep.type === "email" ? "email"
                    : currentStep.type === "tel" ? "tel"
                    : currentStep.type === "number" ? "numeric"
                    : undefined
                  }
                  autoComplete={
                    currentStep.field === "email" ? "email"
                    : currentStep.field === "mobile" ? "tel"
                    : undefined
                  }
                  min={currentStep.min}
                  max={currentStep.max}
                  maxLength={currentStep.maxLength}
                  placeholder={currentStep.placeholder}
                  value={formData[currentStep.field] ?? ""}
                  onChange={(e) => updateField(currentStep.field, e.target.value)}
                  onKeyDown={handleKeyDown}
                  list={currentStep.field === "origin_city" ? "origin-city-list" : undefined}
                  {...(isRTL && currentStep.field === "mobile"
                    ? { dir: "rtl", style: { textAlign: "right" } }
                    : {})}
                  {...(isRTL && (currentStep.field === "email" || currentStep.field === "instagram")
                    ? { dir: "ltr", style: { textAlign: "left" } }
                    : {})}
                />
                {currentStep.field === "origin_city" && (
                  <datalist id="origin-city-list">
                    {citiesFor(formData.origin_country).map((c) => (
                      <option key={c.en} value={isRTL ? c.ar : c.en}>{isRTL ? c.en : c.ar}</option>
                    ))}
                  </datalist>
                )}
                </>
              )}

              <div className="mt-8 flex items-center gap-4 flex-wrap">
                <button
                  onClick={handleNext}
                  data-testid="next-btn"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {t("ok")} <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleBack}
                  data-testid="back-inline-btn"
                  className="btn-ghost inline-flex items-center gap-2"
                >
                  <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} /> {t("back")}
                </button>
                <span className="font-body text-xs text-white/30 tracking-wider hidden sm:inline">
                  {t("pressEnter")}{" "}
                  <kbd className="px-1.5 py-0.5 border border-white/20 text-white/60 mx-1">
                    {t("enterKey")}
                  </kbd>{" "}
                  ↵
                </span>
              </div>
            </motion.div>
          )}

          {phase === "upload" && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl w-full"
            >
              <div className="flex items-start gap-3 mb-8">
                <span className="font-heading text-[#F5C518] text-3xl font-semibold">{STEPS.length + 1}</span>
                <ArrowRight className={`w-4 h-4 text-[#F5C518] mt-2 ${isRTL ? "rotate-180" : ""}`} />
                <h2 className="font-heading font-semibold text-white text-2xl sm:text-3xl md:text-4xl leading-tight">
                  {t("uploadHeadL1")}<span className="text-[#F5C518]">.</span>
                </h2>
              </div>

              <p className="font-body text-white/50 text-sm sm:text-base mb-8 ms-8">
                {t("uploadSub")}
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <PhotoSlot
                  label={t("frontPhoto")}
                  required
                  path={photos.front}
                  uploading={uploading}
                  onUpload={(f) => handleUpload("front", f)}
                  onClear={() => setPhotos({ ...photos, front: null })}
                  testId="upload-front"
                  clickLabel={t("clickToUpload")}
                />
                <PhotoSlot
                  label={t("rearPhoto")}
                  required
                  path={photos.rear}
                  uploading={uploading}
                  onUpload={(f) => handleUpload("rear", f)}
                  onClear={() => setPhotos({ ...photos, rear: null })}
                  testId="upload-rear"
                  clickLabel={t("clickToUpload")}
                />
                <PhotoSlot
                  label={t("leftPhoto")}
                  required
                  path={photos.left}
                  uploading={uploading}
                  onUpload={(f) => handleUpload("left", f)}
                  onClear={() => setPhotos({ ...photos, left: null })}
                  testId="upload-left"
                  clickLabel={t("clickToUpload")}
                />
                <PhotoSlot
                  label={t("rightPhoto")}
                  required
                  path={photos.right}
                  uploading={uploading}
                  onUpload={(f) => handleUpload("right", f)}
                  onClear={() => setPhotos({ ...photos, right: null })}
                  testId="upload-right"
                  clickLabel={t("clickToUpload")}
                />
              </div>

              <div className="mt-8 flex items-center gap-4">
                <button onClick={handleBack} className="btn-ghost inline-flex items-center gap-2" data-testid="back-btn">
                  <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} /> {t("back")}
                </button>
                <button
                  onClick={handleNext}
                  data-testid="continue-btn"
                  className="btn-primary inline-flex items-center gap-2"
                  disabled={!photos.front || !photos.rear || !photos.left || !photos.right || uploading}
                >
                  {t("continue")} <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </button>
              </div>
            </motion.div>
          )}

          {phase === "standards" && (
            <motion.div
              key="standards"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl w-full"
            >
              <div className="flex items-start gap-3 mb-8">
                <span className="font-heading text-[#F5C518] text-3xl font-semibold">{STEPS.length + 2}</span>
                <ArrowRight className={`w-4 h-4 text-[#F5C518] mt-2 ${isRTL ? "rotate-180" : ""}`} />
                <h2 className="font-heading font-semibold text-white text-2xl sm:text-3xl md:text-4xl leading-tight">
                  {t("standardsHeadL1")}<span className="text-[#F5C518]">.</span>
                </h2>
              </div>

              <p className="font-body text-white text-base sm:text-lg font-bold mb-8 ms-8">{renderWithGathering(t("standardsSub"))}</p>

              <div className="space-y-3">
                {RULES.map((rule, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const next = [...rulesAgreed];
                      next[idx] = !next[idx];
                      setRulesAgreed(next);
                    }}
                    data-testid={`rule-${idx}`}
                    className="w-full flex items-start gap-4 p-5 border border-white/10 hover:border-white/25 text-start transition-all bg-white/[0.02]"
                  >
                    <div className={`lux-checkbox mt-0.5 ${rulesAgreed[idx] ? "checked" : ""}`}>
                      {rulesAgreed[idx] && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
                    </div>
                    <span className="font-body text-white/90 text-sm sm:text-base flex-1">{renderWithGathering(rule)}</span>
                  </button>
                ))}
              </div>

              <div className="mt-10 flex items-center gap-4">
                <button onClick={handleBack} className="btn-ghost inline-flex items-center gap-2">
                  <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} /> {t("back")}
                </button>
                <button
                  onClick={handleSubmit}
                  data-testid="submit-application-btn"
                  disabled={!allRulesAgreed || submitting}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> {t("submitting")}
                    </>
                  ) : (
                    <>
                      {t("submitApplication")} <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom back button */}
      {phase === "questions" && (
        <div className={`fixed bottom-6 ${isRTL ? "right-6" : "left-6"} z-40`}>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-xs text-white/30 hover:text-white/70 tracking-[0.15em] uppercase transition-colors font-body"
            data-testid="back-step-btn"
          >
            <ArrowLeft className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} /> {t("back")}
          </button>
        </div>
      )}
    </div>
  );
}

function PhotoSlot({ label, required, path, uploading, onUpload, onClear, testId, clickLabel }) {
  const inputId = `file-${testId}`;
  const fileUrl = path ? `${API}/files/${path}` : null;

  return (
    <div className="relative">
      <div className="label-kicker mb-2">
        {label} {required && <span className="text-[#F5C518]">*</span>}
      </div>
      {path ? (
        <div className="relative border border-[#F5C518]/40 bg-black aspect-[4/3] overflow-hidden group">
          <img src={fileUrl} alt="" className="w-full h-full object-cover" />
          <button
            onClick={onClear}
            className="absolute top-2 right-2 p-1.5 bg-black/80 border border-white/20 text-white hover:border-red-500 hover:text-red-500 transition-colors"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="border-2 border-dashed border-white/15 hover:border-[#F5C518]/60 bg-white/[0.02] aspect-[4/3] flex flex-col items-center justify-center cursor-pointer transition-colors group"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-[#F5C518] animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6 text-white/30 group-hover:text-[#F5C518] mb-2 transition-colors" />
              <span className="font-body text-xs text-white/40 tracking-wider uppercase">
                {clickLabel}
              </span>
            </>
          )}
          <input
            id={inputId}
            data-testid={testId}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onUpload(e.target.files[0])}
          />
        </label>
      )}
    </div>
  );
}
